import {
    COA_CASCADED_UPDATE_SOURCE_TYPES,
    getSourceTypeTemplate,
    HISTORY_FIELD_LABELS,
    HistoryGroupBy,
    toInternalHistorySourceType,
} from "@common/constants/history-source-types";
import { isTinAccessHistorySourceType } from "@common/constants/history/tin-access-sources";
import { pluralizeWithCount } from "@common/helpers/string";
import { AuditLogChangeType, ResourceType } from "@common/types/auditLog";
import {
    APIProcessName,
    SQSProcessName,
} from "@common/constants/process-names";
import { HistoryRow } from "../views/historyViewConfig";
import { integrationActors } from "../components/integrationActors";
import {
    isAccrualEffectiveCountSource,
    isAccrualMatchSourceType,
} from "./accrualApproveSourceTypes";
import { ActivityDescriptionParts } from "./activityDescriptionTypes";
import {
    getDiffValueDescriptionParts,
    getReconciliationDescription,
} from "./activityDescriptionDetails";
import { getCoaTemplateApplyDescriptionParts } from "./coaTemplateApplyDescription";
import { getScheduleActivityDescriptionParts } from "./scheduleActivityDescription";

export const CHANGE_TYPE_VERB: Record<AuditLogChangeType, string> = {
    [AuditLogChangeType.CREATE]: "Created",
    [AuditLogChangeType.UPDATE]: "Updated",
    [AuditLogChangeType.DELETE]: "Deleted",
    [AuditLogChangeType.DOWNLOAD]: "Downloaded",
};

export function resolveChangeTypeVerb(
    changeType: AuditLogChangeType,
    sourceType: string,
    fallbackVerb: string,
): string {
    const isReversalCreate =
        changeType === AuditLogChangeType.CREATE &&
        toInternalHistorySourceType(sourceType) ===
            APIProcessName.ACCOUNTING_JOURNAL_ENTRIES_UPDATE;
    if (isReversalCreate) {
        return "Reversed";
    }
    // Accrual match/unmatch logs already carry their own bespoke verb in
    // the source-type template ("Accrual matched" / "Accrual unmatched") —
    // don't override with the generic CHANGE_TYPE_VERB.
    if (isAccrualMatchSourceType(sourceType)) {
        return fallbackVerb;
    }
    return CHANGE_TYPE_VERB[changeType] ?? fallbackVerb;
}

export function shouldOverrideIntegrationVerb(
    sourceType: string,
    changeTypes: AuditLogChangeType[] | undefined,
): boolean {
    if (!integrationActors[sourceType]) {
        return false;
    }
    if (!changeTypes?.length) {
        return false;
    }
    return changeTypes.every(
        (changeType) => changeType !== AuditLogChangeType.CREATE,
    );
}

interface LiftedChildRowParams {
    sourceType: string;
    changeTypes: AuditLogChangeType[] | undefined;
    resourceIds: string[] | undefined;
    sourceIds: string[] | undefined;
}

function isLiftedChildRow({
    sourceType,
    changeTypes,
    resourceIds,
    sourceIds,
}: LiftedChildRowParams): boolean {
    if (
        !COA_CASCADED_UPDATE_SOURCE_TYPES.includes(sourceType) ||
        changeTypes?.[0] !== AuditLogChangeType.UPDATE
    ) {
        return false;
    }
    return countOwnRows({ resourceIds, sourceIds }) === 0;
}

interface OwnRowsParams {
    resourceIds: string[] | undefined;
    sourceIds: string[] | undefined;
}

function countOwnRows({ resourceIds, sourceIds }: OwnRowsParams): number {
    const sourceIdSet = new Set(sourceIds ?? []);
    return (
        resourceIds?.filter((resourceId) => sourceIdSet.has(resourceId))
            .length ?? 0
    );
}

interface MergedSourceCountParams extends OwnRowsParams {
    sourceType: string;
    changeTypes: AuditLogChangeType[] | undefined;
}

// A partial merge archives its source with an UPDATE row that the daily
// digest folds together with the target's role row, so only the sources
// count as merged accounts.
function resolveMergedSourceCount({
    sourceType,
    changeTypes,
    resourceIds,
    sourceIds,
}: MergedSourceCountParams): number | undefined {
    if (
        toInternalHistorySourceType(sourceType) !==
            APIProcessName.COA_ACCOUNT_MERGE ||
        changeTypes?.[0] !== AuditLogChangeType.UPDATE
    ) {
        return undefined;
    }
    const ownRows = countOwnRows({ resourceIds, sourceIds });
    return ownRows > 0 ? ownRows : undefined;
}

interface HeaderVerbParams {
    sourceType: string;
    changeTypes: AuditLogChangeType[] | undefined;
    resourceIds: string[] | undefined;
    sourceIds: string[] | undefined;
    templateVerb: string | undefined;
    isUpdated: boolean | undefined;
}

export function resolveHeaderVerb({
    sourceType,
    changeTypes,
    resourceIds,
    sourceIds,
    templateVerb,
    isUpdated,
}: HeaderVerbParams): string {
    if (shouldOverrideIntegrationVerb(sourceType, changeTypes)) {
        return "Updated";
    }
    if (isLiftedChildRow({ sourceType, changeTypes, resourceIds, sourceIds })) {
        return "Updated";
    }
    return templateVerb ?? (isUpdated ? "Updated" : "Created");
}

const isTinRevealRow = ({ sourceType }: Pick<HistoryRow, "sourceType">) =>
    sourceType === APIProcessName.COUNTERPARTY_TIN_REVEAL;

const isTinExportRow = ({ sourceType }: Pick<HistoryRow, "sourceType">) =>
    sourceType === APIProcessName.COUNTERPARTY_TIN_EXPORT;

export const isTinAccessRow = ({
    sourceType,
}: Pick<HistoryRow, "sourceType">) => isTinAccessHistorySourceType(sourceType);

const joinParts = (parts: ActivityDescriptionParts): string =>
    [parts.prefix, parts.counter, parts.suffix].filter(Boolean).join("\u00A0");

const ACCRUAL_RESOURCE_TYPES = new Set<ResourceType>([
    ResourceType.BILL,
    ResourceType.INVOICE,
]);

export function getEffectiveCount(row: HistoryRow): number {
    const mergedSourceCount = resolveMergedSourceCount({
        sourceType: row.sourceType,
        changeTypes: row.changeTypes,
        resourceIds: row.historyItem.resourceIds,
        sourceIds: row.historyItem.sourceIds,
    });
    if (mergedSourceCount !== undefined) {
        return mergedSourceCount;
    }
    if (
        ACCRUAL_RESOURCE_TYPES.has(row.resourceType) ||
        isAccrualEffectiveCountSource(row.sourceType)
    ) {
        const uniqueResourceIds = new Set(row.historyItem.resourceIds ?? []);
        return uniqueResourceIds.size || row.count;
    }
    return row.count;
}

export const getActivityDescriptionParts = (
    row: HistoryRow,
): ActivityDescriptionParts => {
    const { sourceType, resourceType, fields, resources, historyItem } = row;
    const count = getEffectiveCount(row);
    const coaTemplateApplyDescription =
        getCoaTemplateApplyDescriptionParts(row);

    if (coaTemplateApplyDescription) {
        return coaTemplateApplyDescription;
    }

    const scheduleActivityDescription =
        getScheduleActivityDescriptionParts(row);
    if (scheduleActivityDescription) {
        return scheduleActivityDescription;
    }

    if (sourceType === APIProcessName.TRANSACTION_SPLIT_V2_UPDATE) {
        return {
            prefix: "Updated split",
            counter: pluralizeWithCount("transaction", count),
            suffix: "",
        };
    }

    if (resourceType === ResourceType.BANK_RECONCILIATION) {
        return {
            prefix: getReconciliationDescription(resources),
            counter: "",
            suffix: "",
        };
    }

    if (historyItem.groupBy === HistoryGroupBy.DIFF_VALUE) {
        return getDiffValueDescriptionParts(row);
    }

    const template = getSourceTypeTemplate(sourceType, resourceType, fields);
    if (isTinRevealRow(row) && template) {
        const counterpartyName = resources?.[0]?.name;
        return counterpartyName
            ? {
                  prefix: `${template.verb} for`,
                  counter: counterpartyName,
                  suffix: "",
              }
            : { prefix: template.verb, counter: "", suffix: "" };
    }

    if (isTinExportRow(row) && template) {
        const isSingle = count === 1;
        const counterpartyName = isSingle ? resources?.[0]?.name : undefined;
        const verb = isSingle
            ? template.verb
            : (template.pluralVerb ?? template.verb);
        return {
            prefix: `${verb} for`,
            counter:
                counterpartyName ?? pluralizeWithCount(template.noun, count),
            suffix: "",
        };
    }

    if (
        resourceType === ResourceType.REPORT_SETTINGS &&
        historyItem.groupBy === HistoryGroupBy.SOURCE_COUNT_DISTINCT &&
        template
    ) {
        return {
            prefix: `${template.verb} ${template.noun}`,
            counter:
                count > 1 ? `(${pluralizeWithCount("action", count)})` : "",
            suffix: "",
        };
    }

    const overrideIntegrationVerb = shouldOverrideIntegrationVerb(
        sourceType,
        row.changeTypes,
    );

    if (
        historyItem.groupBy === HistoryGroupBy.SOURCE_COUNT_DISTINCT &&
        template
    ) {
        const changeType = row.changeTypes[0];
        const baseVerb = overrideIntegrationVerb ? "Updated" : template.verb;
        const verb = changeType
            ? resolveChangeTypeVerb(changeType, sourceType, baseVerb)
            : baseVerb;
        return {
            prefix: verb,
            counter: pluralizeWithCount(template.noun, count),
            suffix: "",
        };
    }

    if (template) {
        if (
            sourceType === APIProcessName.FINANCIAL_DOCUMENT_BULK_ACTION &&
            row.changeTypes[0] === AuditLogChangeType.DELETE
        ) {
            return formatDescriptionParts(
                { ...template, verb: "Deleted" },
                count,
                fields,
            );
        }

        if (
            isLiftedChildRow({
                sourceType,
                changeTypes: row.changeTypes,
                resourceIds: historyItem.resourceIds,
                sourceIds: historyItem.sourceIds,
            })
        ) {
            return formatDescriptionParts(
                { ...template, verb: "Updated" },
                count,
                fields,
            );
        }

        const mergedSourceCount = resolveMergedSourceCount({
            sourceType,
            changeTypes: row.changeTypes,
            resourceIds: historyItem.resourceIds,
            sourceIds: historyItem.sourceIds,
        });
        if (mergedSourceCount !== undefined) {
            return formatDescriptionParts(template, mergedSourceCount);
        }

        const shouldUseDefaultUpdateVerb =
            row.historyItem.groupBy === HistoryGroupBy.FIELD &&
            row.changeTypes[0] === AuditLogChangeType.UPDATE &&
            template.verb === "Updated" &&
            sourceType !== APIProcessName.AUDIT_LOG_REVERT &&
            sourceType !== SQSProcessName.AUDIT_LOG_ROLLBACK;
        const verb =
            overrideIntegrationVerb || shouldUseDefaultUpdateVerb
                ? "Updated"
                : template.verb;
        return formatDescriptionParts({ ...template, verb }, count, fields);
    }

    return formatSourceTypeAsActionParts(sourceType, count);
};

export const getActivityDescription = (row: HistoryRow): string =>
    joinParts(getActivityDescriptionParts(row));

const formatFieldLabel = (field: string): string =>
    HISTORY_FIELD_LABELS[field as keyof typeof HISTORY_FIELD_LABELS];

const formatDescriptionParts = (
    template: {
        verb: string;
        noun: string;
        suffix?: string;
        skipFieldLabel?: boolean;
    },
    count: number,
    fields?: string[],
): ActivityDescriptionParts => {
    const counter = pluralizeWithCount(template.noun, count);
    const suffix = template.suffix ?? "";

    if (template.skipFieldLabel) {
        return { prefix: template.verb, counter, suffix };
    }

    const fieldLabel =
        fields?.length && fields.length > 0 ? formatFieldLabel(fields[0]) : "";
    const prefix = fieldLabel
        ? `${template.verb} ${fieldLabel} for`
        : template.verb;

    return { prefix, counter, suffix };
};

const formatSourceTypeAsActionParts = (
    sourceType: string,
    count: number,
): ActivityDescriptionParts => {
    const formatted = sourceType
        .replace(/-/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

    if (count > 1) {
        return {
            prefix: formatted,
            counter: `(${count} items)`,
            suffix: "",
        };
    }
    return { prefix: formatted, counter: "", suffix: "" };
};
